import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faFileImage,
  faFileCode,
  faFilePdf,
  faFileAlt,
  faExternalLinkAlt,
  faEdit,
  faEye,
  faDownload,
  faClock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge, getTicketStatusBadge, getIncidentTimeInfo, getFileMeta } from './tagStatusHelper';

/**
 * Modal de visualización de incidencia abierta
 * Diseño idéntico a la maqueta del usuario (Imagen 2).
 * Muestra el identificador `#INC-{idEtiqueta}`, estado, descripción,
 * archivos adjuntos y lista de tickets vinculados con botón para resolver.
 */
const TagViewModal = ({
  isOpen,
  selectedTag,
  photosList = [],
  isLoadingPhotos = false,
  tagTickets = [],
  isLoadingTagTickets = false,
  onClose,
  onOpenEdit,
  onOpenComplete,
  onOpenLightbox,
  isDark = false
}) => {
  if (!isOpen || !selectedTag) return null;

  const statusInfo = getStatusBadge(selectedTag.iEstadoIncidencia, isDark);
  const timeInfo = getIncidentTimeInfo(selectedTag);
  const totalTickets = selectedTag.iTotalTickets || tagTickets.length || 0;
  const isResolved = Number(selectedTag.iEstadoIncidencia) === 3;

  // Descargar archivo o imagen adjunta
  const handleDownloadFile = photo => {
    if (!photo?.vUrlFoto) return;
    const link = document.createElement('a');
    link.href = photo.vUrlFoto;
    link.download = photo.vNombreArchivo || `evidencia_INC_${selectedTag.idEtiqueta}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Renderizar icono por tipo de archivo
  const renderAttachmentIcon = fileName => {
    const { type } = getFileMeta(fileName);
    switch (type) {
      case 'image':
        return <FontAwesomeIcon icon={faFileImage} style={{ color: '#6366f1' }} />;
      case 'code':
        return <FontAwesomeIcon icon={faFileCode} style={{ color: '#0ea5e9' }} />;
      case 'pdf':
        return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#ef4444' }} />;
      default:
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#64748b' }} />;
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1050 }}
      tabIndex="-1"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: '640px', width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="modal-content border-0 shadow-2xl"
          style={{
            backgroundColor: isDark ? '#141e2c' : '#ffffff',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
        >
          {/* Header del Modal */}
          <div className="p-4 pb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                {/* ID de la incidencia: #INC-125 */}
                <span
                  className="badge rounded-pill fw-bold"
                  style={{
                    backgroundColor: isDark ? '#1e2c3d' : '#e0e7ff',
                    color: isDark ? '#818cf8' : '#4338ca',
                    fontSize: '0.80rem',
                    letterSpacing: '0.3px',
                    padding: '5px 12px'
                  }}
                >
                  #INC-{selectedTag.idEtiqueta}
                </span>

                {/* Estatus */}
                <span
                  className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                  style={{
                    backgroundColor: statusInfo.bg,
                    color: statusInfo.color,
                    border: `1px solid ${statusInfo.border}`,
                    fontSize: '0.78rem',
                    padding: '5px 12px',
                    gap: '7px'
                  }}
                >
                  <span
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      backgroundColor: statusInfo.color,
                      flexShrink: 0,
                      marginRight: '2px'
                    }}
                  />
                  <span>{statusInfo.label}</span>
                </span>
              </div>

              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                {onOpenEdit && !isResolved && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-1 text-muted hover-opacity shadow-none text-decoration-none"
                    onClick={() => {
                      onClose();
                      onOpenEdit(selectedTag);
                    }}
                    title="Editar incidencia"
                    style={{ fontSize: '0.9rem' }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm p-1 rounded-circle border-0 text-muted hover-opacity shadow-none"
                  onClick={onClose}
                  style={{ fontSize: '1rem', width: '30px', height: '30px' }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {/* Título de la Incidencia */}
            <h4
              className="fw-bold mb-2"
              style={{
                color: isDark ? '#f8fafc' : '#0f172a',
                fontSize: '1.4rem',
                letterSpacing: '-0.3px',
                lineHeight: '1.3'
              }}
            >
              {selectedTag.vNombre}
            </h4>

            {/* Subtítulo con metadata de apertura/resolución y responsable */}
            <div
              className="small d-flex align-items-center flex-wrap"
              style={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: '0.83rem',
                gap: '10px',
                rowGap: '6px'
              }}
            >
              <span className="d-inline-flex align-items-center" style={{ gap: '6px' }}>
                <FontAwesomeIcon
                  icon={timeInfo.isResolved ? faCheckCircle : faClock}
                  style={{
                    fontSize: '0.80rem',
                    color: timeInfo.isResolved ? '#10b981' : '#6366f1'
                  }}
                />
                <span
                  style={{
                    color: timeInfo.isResolved ? (isDark ? '#34d399' : '#059669') : undefined,
                    fontWeight: timeInfo.isResolved ? '600' : 'normal'
                  }}
                >
                  {timeInfo.label}
                </span>
              </span>
              <span className="opacity-40" style={{ userSelect: 'none' }}>•</span>
              <span>
                Creada por{' '}
                <strong style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                  {selectedTag.vCreatedUser || 'Sistema'}
                </strong>
              </span>
              {selectedTag.vResponsable && (
                <>
                  <span className="opacity-40" style={{ userSelect: 'none' }}>•</span>
                  <span>
                    Responsable:{' '}
                    <strong style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }}>
                      {selectedTag.vResponsable}
                    </strong>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Cuerpo del Modal */}
          <div
            className="modal-body p-4 pt-2 d-flex flex-column"
            style={{
              maxHeight: '68vh',
              overflowY: 'auto',
              gap: '20px'
            }}
          >
            {/* Sección: Descripción */}
            <div>
              <h6
                className="fw-bold"
                style={{
                  color: isDark ? '#cbd5e1' : '#1e293b',
                  fontSize: '0.90rem',
                  marginBottom: '9px',
                  letterSpacing: '0.2px'
                }}
              >
                Descripción
              </h6>
              <div
                className="rounded-3"
                style={{
                  backgroundColor: isDark ? '#1a2636' : '#f8fafd',
                  border: `1px solid ${isDark ? '#293a4f' : '#e2e8f0'}`,
                  color: isDark ? '#e2e8f0' : '#334155',
                  fontSize: '0.86rem',
                  lineHeight: '1.6',
                  padding: '14px 16px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {selectedTag.vDescripcion || (
                  <span className="fst-italic text-muted">Sin descripción detallada.</span>
                )}
              </div>
            </div>

            {/* Sección: Archivos y Fotos Adjuntos */}
            <div>
              <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '9px' }}>
                <h6
                  className="fw-bold mb-0"
                  style={{
                    color: isDark ? '#cbd5e1' : '#1e293b',
                    fontSize: '0.90rem',
                    letterSpacing: '0.2px'
                  }}
                >
                  Archivos y Fotos Adjuntos
                </h6>
                <span
                  className="badge rounded-pill fw-medium"
                  style={{
                    fontSize: '0.74rem',
                    backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                    color: isDark ? '#94a3b8' : '#64748b',
                    padding: '3px 10px'
                  }}
                >
                  {photosList.length} archivo{photosList.length === 1 ? '' : 's'}
                </span>
              </div>

              {isLoadingPhotos ? (
                <div className="text-center py-3 text-muted small">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Cargando archivos adjuntos...
                </div>
              ) : photosList.length === 0 ? (
                <div
                  className="rounded-3 text-center border border-dashed"
                  style={{
                    backgroundColor: isDark ? 'rgba(26, 38, 54, 0.4)' : '#f8fafc',
                    borderColor: isDark ? '#293a4f' : '#e2e8f0',
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontSize: '0.84rem',
                    padding: '16px 20px'
                  }}
                >
                  No hay archivos o evidencias adjuntas en esta incidencia.
                </div>
              ) : (
                <div className="d-flex flex-column" style={{ gap: '10px' }}>
                  {photosList.map(item => {
                    const meta = getFileMeta(item.vNombreArchivo);
                    return (
                      <div
                        key={item.idIncidenciaFoto}
                        className="rounded-3 border d-flex align-items-center justify-content-between"
                        style={{
                          backgroundColor: isDark ? '#1a2636' : '#ffffff',
                          borderColor: isDark ? '#293a4f' : '#e2e8f0',
                          padding: '11px 15px'
                        }}
                      >
                        <div className="d-flex align-items-center overflow-hidden" style={{ gap: '12px' }}>
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: '38px',
                              height: '38px',
                              backgroundColor: isDark ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff',
                              fontSize: '1.05rem'
                            }}
                          >
                            {renderAttachmentIcon(item.vNombreArchivo)}
                          </div>
                          <div className="overflow-hidden">
                            <div
                              className="fw-semibold text-truncate"
                              style={{
                                fontSize: '0.85rem',
                                color: isDark ? '#f8fafc' : '#0f172a',
                                maxWidth: '320px'
                              }}
                              title={item.vNombreArchivo || 'Evidencia'}
                            >
                              {item.vNombreArchivo || 'archivo-evidencia'}
                            </div>
                            <div className="small text-muted" style={{ fontSize: '0.73rem', marginTop: '2px' }}>
                              {meta.ext} • {item.vDescripcion || 'Adjunto'}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '10px' }}>
                          {meta.type === 'image' && onOpenLightbox ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-primary text-decoration-none fw-semibold shadow-none"
                              style={{ fontSize: '0.82rem' }}
                              onClick={() => onOpenLightbox(item)}
                            >
                              Ver
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-primary text-decoration-none fw-semibold shadow-none"
                              style={{ fontSize: '0.82rem' }}
                              onClick={() => handleDownloadFile(item)}
                            >
                              Ver
                            </button>
                          )}
                          <span className="text-muted opacity-40">•</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-muted text-decoration-none fw-semibold hover-opacity shadow-none"
                            style={{ fontSize: '0.82rem' }}
                            onClick={() => handleDownloadFile(item)}
                          >
                            Descargar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sección: Tickets Asignados */}
            <div>
              <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '9px' }}>
                <h6
                  className="fw-bold mb-0"
                  style={{
                    color: isDark ? '#cbd5e1' : '#1e293b',
                    fontSize: '0.90rem',
                    letterSpacing: '0.2px'
                  }}
                >
                  Tickets Asignados
                </h6>
                <span
                  className="badge rounded-pill fw-medium"
                  style={{
                    fontSize: '0.74rem',
                    backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                    color: isDark ? '#94a3b8' : '#64748b',
                    padding: '3px 10px'
                  }}
                >
                  {totalTickets} asociado{totalTickets === 1 ? '' : 's'}
                </span>
              </div>

              {isLoadingTagTickets ? (
                <div className="text-center py-3 text-muted small">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Cargando tickets asociados...
                </div>
              ) : tagTickets.length === 0 ? (
                <div
                  className="rounded-3 text-center border border-dashed"
                  style={{
                    backgroundColor: isDark ? 'rgba(26, 38, 54, 0.4)' : '#f8fafc',
                    borderColor: isDark ? '#293a4f' : '#e2e8f0',
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontSize: '0.84rem',
                    padding: '16px 20px'
                  }}
                >
                  Aún no hay tickets vinculados a esta incidencia.
                </div>
              ) : (
                <div className="d-flex flex-column" style={{ gap: '10px' }}>
                  {tagTickets.map(ticket => {
                    const ticketBadge = getTicketStatusBadge(ticket.iEstadoTicket || 1, isDark);
                    const ticketUrl = `/support-engine/ticket/${ticket.idTicket}`;

                    return (
                      <div
                        key={ticket.idTicket}
                        className="rounded-3 border d-flex align-items-center justify-content-between"
                        style={{
                          backgroundColor: isDark ? '#1a2636' : '#ffffff',
                          borderColor: isDark ? '#293a4f' : '#e2e8f0',
                          padding: '12px 16px',
                          gap: '14px'
                        }}
                      >
                        <div className="d-flex align-items-center overflow-hidden" style={{ gap: '14px' }}>
                          <a
                            href={ticketUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fw-bold text-decoration-none d-inline-flex align-items-center flex-shrink-0"
                            title={`Abrir ticket #${ticket.idTicket}`}
                            style={{
                              color: isDark ? '#818cf8' : '#4f46e5',
                              backgroundColor: isDark ? 'rgba(99, 102, 241, 0.12)' : '#eef2ff',
                              padding: '5px 10px',
                              borderRadius: '8px',
                              fontSize: '0.82rem',
                              gap: '6px'
                            }}
                          >
                            <span>#TCK-{ticket.idTicket}</span>
                            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.68rem', opacity: 0.8 }} />
                          </a>

                          <div className="overflow-hidden d-flex flex-column justify-content-center" style={{ gap: '2px' }}>
                            <div
                              className="fw-semibold text-truncate"
                              style={{
                                fontSize: '0.86rem',
                                color: isDark ? '#f8fafc' : '#0f172a',
                                maxWidth: '280px'
                              }}
                            >
                              {ticket.vCustomerName || 'Cliente sin registrar'}
                            </div>
                            <div
                              className="small text-muted text-truncate"
                              style={{ fontSize: '0.75rem', maxWidth: '300px' }}
                            >
                              {ticket.vAsunto || ticket.vUltimoMensaje || 'Reporte de cliente'}
                            </div>
                          </div>
                        </div>

                        <span
                          className="badge rounded-pill fw-semibold flex-shrink-0"
                          style={{
                            backgroundColor: ticketBadge.bg,
                            color: ticketBadge.color,
                            border: `1px solid ${ticketBadge.border}`,
                            fontSize: '0.74rem',
                            padding: '5px 12px'
                          }}
                        >
                          {ticketBadge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer del Modal */}
          <div
            className="modal-footer border-top px-4 py-3 d-flex align-items-center justify-content-end"
            style={{
              borderColor: isDark ? '#223247' : '#e2e8f0',
              gap: '12px'
            }}
          >
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none fw-semibold shadow-none"
              onClick={onClose}
              style={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: '0.86rem',
                padding: '8px 16px'
              }}
            >
              Cerrar ventana
            </button>

            {!isResolved && (
              <button
                type="button"
                className="btn btn-sm rounded-3 fw-semibold d-inline-flex align-items-center shadow-none text-white"
                onClick={() => {
                  onClose();
                  onOpenComplete(selectedTag);
                }}
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#0f172a',
                  border: `1px solid ${isDark ? '#334155' : '#0f172a'}`,
                  fontSize: '0.86rem',
                  padding: '8px 18px',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.80rem' }} />
                <span>Cerrar Incidencia</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagViewModal;
